import React, { ReactElement, useMemo, useRef, useState } from 'react'
import { Profile } from 'store/api/profiles/profiles.interface'
import { SimpleActionMenuItemProps, Svg } from 'ui'
import { Flex } from '@theme-ui/components'
import { Text } from 'theme-ui'
import TextWithOverFlowAndTippyPopup from 'components/TextWithOverFlowAndTippyPopup'
import CheckIcon from 'images/analytics/check.svg'
import { useGetProfilesQuery } from 'store/api/profiles'
import { useGetUnImpersonatedUserQuery } from 'store/api/user'
import SimpleDropdown from 'ui/Dropdown/SimpleDropdown'
import { organizationApi } from 'store/api/organization'
import useBreakpointIndex from 'ui/Theme/useBreakpointIndex'
import useQueryString from 'utils/useQueryString'
import { useAppSelector } from 'store/hooks'
import { useHideTooltipOnScroll } from 'utils/useHideTooltipOnScroll'

export interface ProfileDropDownItem extends Profile {
  text: string
  isShared?: boolean
}

export default function HeaderProfilesDropdown({ profile }: { profile?: Profile }): ReactElement {
  const { data: profilesData } = useGetProfilesQuery('')
  const hideRef = useRef<() => void>()
  const [selectedProfile, setSelectedProfile] = useState<Profile | undefined>(profile)
  const { data: orgData } = organizationApi.endpoints.getOrganization.useQueryState('')
  const globalProfilePk = orgData?.organization?.parent_profile?.PK
  const isMobile = useBreakpointIndex() === 0
  const { relocate } = useQueryString()
  const editingProfileSection = useAppSelector(s => s.profiles.editingProfileSection)

  useHideTooltipOnScroll(hideRef)

  // this is going to return the unimpersonated user data which we use to figure out whether the user
  // is from parent org or sub org
  const { data: userData } = useGetUnImpersonatedUserQuery('')
  const isSubOrgUser = !!userData?.org?.parent_org

  const profileData: ProfileDropDownItem[] = useMemo(
    () =>
      [
        ...(profilesData?.profiles || []).map(p => ({ ...p, text: `${p.name}` })),
        ...(!isSubOrgUser
          ? profilesData?.shared_profiles?.map(p => ({
              ...p,
              text: `${p.name}`,
              isShared: true,
            })) || []
          : []),
      ] ?? [],
    [profilesData?.profiles, profilesData?.shared_profiles, isSubOrgUser],
  )

  const profileOptions: SimpleActionMenuItemProps[] = useMemo(() => {
    const options = profileData.map(profile => ({
      dataTestId: `testid-${profile.text}`,
      ariaLabel: `${profile?.text} option`,
      isSelected: profile.PK === selectedProfile?.PK,
      isBackgroundHighlighted: true,
      searchBy: profile.text,
      buttonContent: (
        <TextWithOverFlowAndTippyPopup
          offset={[0, 4]}
          content={profile?.text ?? ''}
          ariaLabel={profile?.text ?? ''}
          sxText={{
            maxWidth: '44rem',
          }}
        />
      ),
      children: (
        <Flex sx={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
          <Flex
            sx={{
              flexDirection: ['column', 'row'],
              justifyContent: 'space-between',
              flex: 1,
              alignItems: ['flex-start', 'center'],
              mr: profile.PK === selectedProfile?.PK && (profile.isShared ? '0.8rem' : '1.6rem'),
            }}
          >
            <Flex sx={{ width: '100%', gap: '0.8rem', alignItems: 'center' }}>
              <TextWithOverFlowAndTippyPopup
                variant="size15Weight600"
                content={profile?.text ?? ''}
                ariaLabel={profile?.text ?? ''}
              />

              {profile.isShared && (
                <Text
                  variant="size10Weight700"
                  className="shared-profile-badge"
                  sx={{
                    color: 'commonBlack',
                    backgroundColor: 'commonCyan800',
                    px: '0.4rem',
                    borderRadius: '10rem',
                    flexShrink: 0,
                    height: '1.6rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  {profile.PK === globalProfilePk ? 'Global' : 'Shared'}
                </Text>
              )}
            </Flex>
          </Flex>
          {profile.PK === selectedProfile?.PK && (
            <Svg
              svg={CheckIcon}
              sx={{ flexShrink: 0, fill: 'aliceBlue', opacity: 0.5 }}
              descriptiveText="Selected profile indicator"
            />
          )}
        </Flex>
      ),
      onClick: () => {
        setSelectedProfile(profile)
        relocate(`/dashboard/profiles/${profile.PK}/${editingProfileSection || ''}`)
        hideRef.current?.()
      },
    }))

    return options
  }, [globalProfilePk, profileData, selectedProfile, relocate, editingProfileSection])

  return (
    <SimpleDropdown
      isSearchVisible
      items={profileOptions}
      hideRef={hideRef}
      ariaLabel="header profiles dropdown"
      testId="header-profiles-dropdown"
      boundaryElementTestId={isMobile ? 'parent' : 'dashboard-scrolling-container'}
      sx={{
        ':not(.tippy-box)': {
          pr: '0.4rem',
        },
      }}
      sxDropdown={{
        border: 'none',
        minHeight: 'fit-content',
        gap: 0,
        p: 0,
        backgroundColor: 'transparent',
      }}
      sxDropdownButton={{
        width: '100%',
        fontSize: ['1.5rem', '1.7rem'],
        fontWeight: 500,
        '&:hover': {
          color: 'aliceBlue',
        },
      }}
      sxDropdownContent={{
        width: ['65vw', '30rem'],
        maxHeight: '30rem',
      }}
      placement="bottom-start"
    />
  )
}
